import React from 'react';
import type { ReportTheme } from '../../../../types/reportTheme';
import type { EditorialPart, EditorialPartTocEntry } from '../../../../lib/editorial/reportParts';
import { resolveCoverDarkSurface } from '../../../../lib/reportCoverTokens';
import { REPORT_TEXT_PX } from '../../../../lib/reportSectionHeading';

/**
 * C0-GEN Wave 3 — Part-divider FEATURE SHEET (option-C monograph).
 *
 * A DRAMATIC full-bleed DARK interior sheet that opens a narrative Part (II
 * Economics & Power, III Risk & Decision). It mirrors the option-C `.divider`
 * sheet exactly: an ink page filling the whole A4 sheet, the accent "Part II"
 * eyebrow, a giant serif Roman numeral, the part headline + italic continuation
 * clause, a thesis line under a hairline rule, and a small section mini-TOC.
 *
 * Why a dedicated dark surface (not {@link EditorialDarkFeaturePanel}): the
 * part divider is STRUCTURAL chrome — when the editorial monograph is engaged it
 * is ALWAYS a full-page dark feature sheet, never a gated brand affordance.
 * EditorialDarkFeaturePanel's dark mode is opt-in (`darkFeatureRhythm`) and falls
 * back to a small light-grey box on a stock theme, which made the divider read
 * as "a small light box on a blank page". Here the dark surface + on-dark ramp
 * are derived from the SAME `resolveCoverDarkSurface(theme)` the dark covers use,
 * so the divider takes on the active preset's ink/accent and is print-safe.
 *
 * Stamped `data-section` / `data-section-flow="span"` so it rides the page-flow
 * deck as a full-width sheet (never packed into a prose column) and is
 * discoverable as a section subtree. Pure layout; the copy is the deterministic
 * EDITORIAL_PARTS config + the caller-resolved section labels — no generated
 * content, so it is safe regardless of the narrative layer.
 *
 * GATED at the call-site on the editorial chrome being engaged
 * (`isEditorialLayoutsEnabled()` / `editorialActive`); the component itself holds
 * no flag, so it never renders unless the editorial chrome is engaged.
 */
export interface EditorialPartDividerProps {
  theme: ReportTheme;
  part: EditorialPart;
  /**
   * Optional section mini-TOC for this part (the option-C `.d-toc`). When
   * absent / empty the divider still renders its numeral + headline + thesis.
   */
  tocEntries?: EditorialPartTocEntry[];
}

export const EditorialPartDivider: React.FC<EditorialPartDividerProps> = ({
  theme,
  part,
  tocEntries,
}) => {
  const dark = resolveCoverDarkSurface(theme);
  const displayFont = theme.displayFont || theme.headingFont;
  const monoFont = theme.bodyFont;
  const entries = Array.isArray(tocEntries) ? tocEntries : [];

  return (
    <div
      data-section={`part-divider-${part.id}`}
      data-section-id={`part-divider-${part.id}`}
      data-section-flow="span"
      data-editorial-part-divider={part.id}
      data-testid={`editorial-part-divider-${part.id}`}
      style={{
        // A full-bleed dark sheet that FILLS the page (so it never reads as a
        // small box on a blank page). The `breakBefore: page` lives on the
        // call-site WRAPPER (the flow child carrying the section `order`), NOT
        // here — a break on BOTH the wrapper box AND this inner sheet double-broke
        // into a blank leading page. A full-page-height block consumes the whole
        // page so the next section starts on the NEXT sheet. We deliberately do
        // NOT set `breakInside: avoid`: a block whose height ≈ the page box can
        // never satisfy that after a break. Clamp height (cover precedent) so it
        // can't overflow.
        background: dark.surface,
        color: dark.text,
        // Print-safe: keep the dark fill through Chromium / Paged.js.
        WebkitPrintColorAdjust: 'exact',
        printColorAdjust: 'exact',
        // Fill the printable A4 box (matches the cover's clamped min/height) so
        // the sheet is a true full-page feature that always fits ONE content box.
        minHeight: `calc(var(--report-page-height, 297mm) - 14mm - 16mm - 1mm)`,
        maxHeight: `calc(var(--report-page-height, 297mm) - 14mm - 16mm)`,
        overflow: 'hidden',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        padding: '40px 36px',
      }}
    >
      <div style={{ maxWidth: '40em', margin: 'auto 0' }}>
        {/* Accent "Part II" eyebrow. */}
        <div
          style={{
            fontFamily: monoFont,
            fontSize: REPORT_TEXT_PX.label,
            fontWeight: 600,
            letterSpacing: '0.32em',
            textTransform: 'uppercase',
            color: dark.accent,
            marginBottom: 22,
          }}
        >
          {`Part ${part.numeral}`}
        </div>

        {/* Giant serif Roman numeral. */}
        <div
          aria-hidden="true"
          data-editorial-part-numeral={part.id}
          style={{
            fontFamily: displayFont,
            fontWeight: 600,
            fontSize: 128,
            lineHeight: 0.82,
            letterSpacing: '-0.04em',
            fontVariantNumeric: 'tabular-nums',
            color: dark.text,
            marginBottom: 18,
          }}
        >
          {part.numeral}
        </div>

        {/* Part headline + italic continuation clause. */}
        <h2
          data-heading="true"
          style={{
            fontFamily: displayFont,
            fontWeight: 600,
            fontSize: 44,
            lineHeight: 1.04,
            letterSpacing: '-0.015em',
            color: dark.text,
            margin: '0 0 20px',
          }}
        >
          {part.title}
          <span
            style={{
              display: 'block',
              fontStyle: 'italic',
              fontWeight: 400,
              fontSize: 28,
              lineHeight: 1.08,
              color: dark.muted,
              marginTop: 8,
            }}
          >
            {part.subtitle}
          </span>
        </h2>

        {/* Thesis under a hairline rule. */}
        <p
          data-editorial-part-thesis={part.id}
          style={{
            fontFamily: displayFont,
            fontSize: 16,
            lineHeight: 1.5,
            color: dark.faint,
            maxWidth: '36em',
            margin: '0',
            borderTop: `1px solid ${dark.hair}`,
            paddingTop: 18,
          }}
        >
          {part.thesis}
        </p>

        {/* Section mini-TOC (the option-C `.d-toc`) — one column per section in
            this part, the divider's opener marked active (accent ink). */}
        {entries.length > 0 && (
          <div
            data-editorial-part-toc={part.id}
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '24px 38px',
              marginTop: 32,
            }}
          >
            {entries.map((entry) => (
              <div
                key={entry.id}
                data-editorial-part-toc-entry={entry.id}
                style={{
                  fontFamily: monoFont,
                  fontSize: REPORT_TEXT_PX.micro,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  color: dark.faint,
                  lineHeight: 1.7,
                  maxWidth: '12em',
                }}
              >
                <span
                  style={{
                    display: 'block',
                    fontSize: REPORT_TEXT_PX.caption,
                    letterSpacing: '0.06em',
                    color: entry.active ? dark.accent : dark.text,
                  }}
                >
                  {`§${entry.numeral}`}
                </span>
                {entry.label}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
